import { EventChannel, eventChannel, Task } from 'redux-saga';
import { call, cancel, cancelled, fork, put, select, take } from 'redux-saga/effects';

import { getSocket } from '../../services/socket';
import { RootState } from '..';
import { Room, TableItem } from '../types';
import {
	roomClose,
	roomError,
	roomLoaded,
	roomOpen,
	tablesAppend,
	tablesRemove,
	tablesSet,
	tablesUpdateSeats,
} from './roomActions';

type EmittedEvent =
	| { type: 'getTables'; data: TableItem[] }
	| { type: 'getTable'; data: TableItem }
	| { type: 'joinTable'; data: { tableID: string | number } }
	| { type: 'exitTable'; data: { tableID: string | number } }
	| { type: 'closeTable'; data: { id: string | number } };

// --- Channel that wires up socket listeners and emits normalized events ---
function socketChannel(
	roomId: string,
	playerId: string,
	username: string,
): EventChannel<EmittedEvent> {
	const socket = getSocket(playerId, username);

	return eventChannel<EmittedEvent>(emit => {
		const onGetTables = (packet: any) => emit({ type: 'getTables', data: packet?.data ?? [] });
		const onLoadedTable = (packet: any) => emit({ type: 'getTable', data: packet?.data });
		const onJoin = (packet: any) => emit({ type: 'joinTable', data: packet?.data });
		const onExit = (packet: any) => emit({ type: 'exitTable', data: packet?.data });
		const onClosed = (packet: any) => emit({ type: 'closeTable', data: packet?.data });

		socket.on('getTables', onGetTables);
		socket.on('getTable', onLoadedTable);
		socket.on('joinTablePublisher', onJoin);
		socket.on('exitTablePublisher', onExit);
		socket.on('getClosedTables', onClosed);

		return () => {
			socket.emit('service:leaveLobbyRoom', { roomId });
			socket.off('getTables', onGetTables);
			socket.off('getTable', onLoadedTable);
			socket.off('joinTablePublisher', onJoin);
			socket.off('exitTablePublisher', onExit);
			socket.off('getClosedTables', onClosed);
		};
	});
}

// --- Saga that consumes the channel and updates Redux state ---
function* handleSocket(roomId: string, playerId: string, username: string) {
	console.log('[ROOM] roomSocket handleSocket start', { roomId, playerId, username });
	const chan: EventChannel<EmittedEvent> = yield call(socketChannel, roomId, playerId, username);

	try {
		while (true) {
			const evt: EmittedEvent = yield take(chan);

			switch (evt.type) {
				case 'getTables': {
					const incoming: TableItem[] = Array.isArray(evt.data) ? evt.data : [];
					const current: TableItem[] = yield select((s: any) => s.room.tables);

					const filtered = incoming.filter(
						t => String(t.room_id) === String(roomId) && !current.find(c => c.id === t.id),
					);

					if (filtered.length) {
						if (current.length === 0) yield put(tablesSet(filtered));
						else yield put(tablesAppend(filtered));
					}
					break;
				}

				case 'getTable': {
					const table = evt.data;
					if (!table || String(table.room_id) !== String(roomId)) break;

					const current: TableItem[] = yield select((s: any) => s.room.tables);
					if (!current.find(t => t.id === table.id)) {
						yield put(tablesAppend([table]));
					}
					break;
				}

				case 'joinTable': {
					yield put(tablesUpdateSeats({ id: evt.data.tableID, inc: true }));
					break;
				}

				case 'exitTable': {
					yield put(tablesUpdateSeats({ id: evt.data.tableID, inc: false }));
					break;
				}

				case 'closeTable': {
					yield put(tablesRemove(evt.data.id));
					break;
				}
			}
		}
	} finally {
		if (yield cancelled()) {
			chan.close();
		}
	}
}

// --- Entry worker: fetch room, gate, then fork socket consumer ---
function* openRoomWorker(action: ReturnType<typeof roomOpen>) {
	try {
		const room: Room = {
			id: String(action.payload.roomId),
			slug: 'sofia',
			name: 'sofia',
			room_type: 'normal',
			min_user_level: 1,
		};
		yield put(roomLoaded(room));

		const roomId = String(action.payload.roomId);
		console.log('[ROOM] roomOpen', { roomId });

		const auth: RootState['auth'] = yield select((state: RootState) => state.auth);
		const playerId = auth.user?.id || 'anonymous';
		const username = auth.user?.username || 'anonymous';

		const task = yield fork(handleSocket, roomId, playerId, username);

		// Closing the channel emits service:leaveLobbyRoom, so this worker must only
		// die on roomClose (screen unmount) or when the room/identity really changes.
		yield take(roomClose.type);
		yield cancel(task);
	} catch (e: any) {
		yield put(roomError(e?.message || 'room_failed'));
	}
}

export function* roomSocketSaga() {
	// Not takeLatest: a repeated roomOpen for the same room (re-render, second visit
	// while the previous worker is still alive) used to cancel the old worker, whose
	// channel teardown emitted service:leaveLobbyRoom right after the fresh
	// service:getTables had joined `lobby:<roomId>`. The socket then sat outside the
	// lobby room and never received joinTablePublisher/exitTablePublisher, so seat
	// changes made on another device did not show (seen 2026-09-04).
	let task: Task | null = null;
	let key: string | null = null;

	while (true) {
		const action: ReturnType<typeof roomOpen> | ReturnType<typeof roomClose> = yield take([
			roomOpen.type,
			roomClose.type,
		]);

		if (action.type === roomClose.type) {
			if (task) yield cancel(task);
			task = null;
			key = null;
			continue;
		}

		const open = action as ReturnType<typeof roomOpen>;
		const auth: RootState['auth'] = yield select((state: RootState) => state.auth);
		const nextKey = `${String(open.payload.roomId)}|${auth.user?.id || 'anonymous'}`;

		if (task && task.isRunning() && key === nextKey) continue;

		if (task) yield cancel(task);
		key = nextKey;
		task = yield fork(openRoomWorker, open);
	}
}
